import io, { Socket } from 'socket.io-client';
import { getFromStorage, saveToStorage } from '../utils/storage';
import { WS_BASE_URL, API_ENDPOINTS, API_BASE_URL } from '../config/api';

class WebSocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private currentNamespace: string = '';
  private reconnectAttempts: number = 0;

  private reconnectDelay: number = 1000;
  private connectionPromise: Promise<void> | null = null;

  async connect(namespace: string = ''): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.socket?.connected && this.currentNamespace === namespace) {
      return Promise.resolve();
    }

    this.connectionPromise = this._connectInternal(namespace);

    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = null;
    }
  }

  private async _connectInternal(namespace: string): Promise<void> {
    if (this.socket && this.currentNamespace !== namespace) {
      this.disconnect();
    }

    let token = await getFromStorage('authToken') || await getFromStorage('access_token');

    if (!token) {
      console.warn('🔵 [WebSocket] No access token found, trying to refresh...');
      try {
        const refreshToken = await getFromStorage('refreshToken') || await getFromStorage('refresh_token');
        if (refreshToken) {
          try {
            const axios = (await import('axios')).default;
            const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`, {
              refresh_token: refreshToken,
            });
            if (response.data.access_token) {
              await saveToStorage('authToken', response.data.access_token);
              await saveToStorage('access_token', response.data.access_token);
              if (response.data.refresh_token) {
                await saveToStorage('refreshToken', response.data.refresh_token);
                await saveToStorage('refresh_token', response.data.refresh_token);
              }
              token = response.data.access_token;
              console.log('🔵 [WebSocket] ✅ Token refreshed successfully');
            }
          } catch (refreshError) {
            console.error('❌ [WebSocket] Failed to refresh token:', refreshError);
          }
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    }

    if (!token) {
      console.warn('No access token found, cannot connect WebSocket');
      return;
    }

    let url = WS_BASE_URL;
    if (namespace) {
      const baseUrl = WS_BASE_URL.replace(/\/$/, '');
      url = `${baseUrl}/${namespace}`;
    }

    this.currentNamespace = namespace;

    this.socket = io(url, {
      auth: {
        token,
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity, 
      timeout: 20000,
      forceNew: false, 
      autoConnect: true,
    });

    const connectPromise = new Promise<void>((resolve, reject) => {
      const connectTimeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 20000);

      const connectHandler = () => {
        clearTimeout(connectTimeout);
        this.reconnectAttempts = 0;
        console.log(`🔵 [WebSocket] ✅ Connected to namespace: ${namespace || 'default'}`);
        console.log(`🔵 [WebSocket] Socket ID: ${this.socket!.id}`);
        this.socket!.off('connect', connectHandler);
        this.socket!.off('connect_error', errorHandler);
        resolve();
      };

      const errorHandler = (error: any) => {
        clearTimeout(connectTimeout);
        this.reconnectAttempts++;
        console.error('WebSocket connection error:', error.message);
        this.socket!.off('connect', connectHandler);
        this.socket!.off('connect_error', errorHandler);
        if (error.message === 'Authentication failed' || error.message?.includes('401')) {
          this.handleAuthError();
        }
        reject(error);
      };

      if (this.socket) {
        this.socket.on('connect', connectHandler);
        this.socket.on('connect_error', errorHandler);
      }
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log(`WebSocket disconnected. Reason: ${reason}`);
      if (reason === 'transport close' || reason === 'client disconnect') {
        console.log('Transport closed, Socket.IO will attempt auto-reconnect');
      } else if (reason === 'io server disconnect') {
        console.log('Server disconnected, attempting to refresh token and reconnect...');
        setTimeout(() => {
          this.handleAuthError();
        }, 2000);
      }
    });

    await connectPromise;
    console.log('🔵 [WebSocket] ✅ Connection established, setting up existing listeners...');
    this.setupExistingListeners();
    console.log('🔵 [WebSocket] ✅ All existing listeners set up');

    this.socket.on('error', (error: any) => {
      console.error('WebSocket error:', error);
    });

    this.socket.on('reconnect', (attemptNumber: number) => {
      console.log(`WebSocket reconnected after ${attemptNumber} attempts`);
      this.reconnectAttempts = 0;
    });

    this.socket.on('reconnect_attempt', (attemptNumber: number) => {
      console.log(`WebSocket reconnection attempt ${attemptNumber}`);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed after all attempts');
    });
  }

  private async handleAuthError() {
    try {

      const refreshToken = await getFromStorage('refreshToken') || await getFromStorage('refresh_token');
      if (refreshToken) {
        try {
          const axios = (await import('axios')).default;
          const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`, {
            refresh_token: refreshToken,
          });

          if (response.data.access_token) {
            await saveToStorage('authToken', response.data.access_token);
            await saveToStorage('access_token', response.data.access_token);
            if (response.data.refresh_token) {
              await saveToStorage('refreshToken', response.data.refresh_token);
              await saveToStorage('refresh_token', response.data.refresh_token);
            }

            if (this.currentNamespace) {
              await this.connect(this.currentNamespace);
            }
          }
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);


        }
      }
    } catch (error) {
      console.error('Error in handleAuthError:', error);
    }
  }

  private setupExistingListeners() {
    console.log(`🔵 [WebSocket] Setting up ${this.listeners.size} event listeners...`);
    this.listeners.forEach((callbacks, event) => {
      console.log(`🔵 [WebSocket] Setting up ${callbacks.size} callback(s) for event: ${event}`);
      callbacks.forEach((callback) => {
        this.socket?.on(event, (...args: any[]) => {
          console.log(`🔵 [WebSocket] ✅ Event received: ${event}`, args.length > 0 ? 'with data' : 'no data');
          callback(...args);
        });
      });
    });
    console.log('🔵 [WebSocket] ✅ All listeners set up');
  }

  on(event: string, callback: (...args: any[]) => void) {
    console.log(`🔵 [WebSocket] Registering listener for event: ${event}`);
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);

    if (this.socket) {
      console.log(`🔵 [WebSocket] Socket exists, attaching listener for: ${event}`);
      this.socket.on(event, (...args: any[]) => {
        console.log(`🔵 [WebSocket] ✅ Event received: ${event}`, args.length > 0 ? 'with data' : 'no data');
        callback(...args);
      });
    } else {
      console.log(`🔵 [WebSocket] ⚠️ Socket not connected yet, listener will be attached on connect for: ${event}`);
    }
  }

  off(event: string, callback?: (...args: any[]) => void) {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
      this.socket?.off(event, callback);
    } else {
      this.listeners.delete(event);
      this.socket?.off(event);
    }
  }

  emit(event: string, data?: any) {
    this.socket?.emit(event, data);
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
    this.currentNamespace = '';
    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getNamespace(): string {
    return this.currentNamespace;
  }

  getConnectionState(): 'connected' | 'connecting' | 'disconnected' {
    if (this.connectionPromise) {
      return 'connecting';
    }
    if (this.socket?.connected) {
      return 'connected';
    }
    return 'disconnected';
  }

  async ensureConnection(namespace: string): Promise<void> {
    if (this.isConnected() && this.currentNamespace === namespace) {
      return;
    }
    await this.connect(namespace);
  }
}

export const wsService = new WebSocketService();
export default wsService;
